import { useMemo } from 'react'

import { MediaTypes, SocialSubmissionTypes, SubmissionChannels } from '@enums'
import { IPostPurchaseBrandConnectAnalytics } from '@store/postPurchaseAnalytics'
import { IPrePurchaseBrandConnectAnalytics } from '@store/prePurchaseAnalytics'
import { Container } from '@tribegroup'
import { Loading } from '@tribegroup/Modal/Modal'
import {
  AnalyticsBreakdownFilter,
  IAnalyticsBreakdownFilterProps,
} from './Filter/AnalyticsBreakdownFilter'
import { AnalyticsBreakdownGlossary } from './Glossary/Glossary'
import { AnalyticsBreakdownMetricTableHeader } from './MetricTable/Header'
import { AnalyticsBreakdownMetricTableSection } from './MetricTable/Section'
import { AnalyticsPerspective, MetricEntry } from './types'

type Props = {
  analytics: IPrePurchaseBrandConnectAnalytics[] | IPostPurchaseBrandConnectAnalytics[]
  socialSubmissionType: SocialSubmissionTypes
  currency: string
  isLoaded: boolean
  isProxy?: boolean
  channel?: SubmissionChannels
  mediaType?: MediaTypes
  sections: Array<{ title: string; entries: MetricEntry[] }>
  customGlossaryEntries?: MetricEntry[]
  perspective?: AnalyticsPerspective
} & IAnalyticsBreakdownFilterProps

export const AnalyticsBrandConnectBreakdown: React.FC<Props> = ({
  currency,
  filters,
  isLoaded,
  socialSubmissionType,
  channel,
  mediaType,
  sections,
  customGlossaryEntries,
  perspective,
}) => {
  const appendV2ToSource = (entries: MetricEntry[]): MetricEntry[] => {
    return entries.map((entry) => ({
      ...entry,
      source: entry.source
        ? `${entry.source}V2${perspective === 'post-purchase' ? '.post_purchase' : ''}`
        : entry.source,
    }))
  }

  const glossaryEntries = useMemo(() => {
    const allSections = sections.flatMap((section) => section.entries)
    return [...appendV2ToSource(allSections), ...(customGlossaryEntries ?? [])]
  }, [sections, customGlossaryEntries])

  if (!isLoaded) {
    return <Loading />
  }

  return (
    <Container relative>
      <Container absolute rightPosition={0} topPosition={-4}>
        <AnalyticsBreakdownGlossary
          submissionType={socialSubmissionType}
          entries={glossaryEntries}
          currency={currency}
          channel={channel}
          mediaType={mediaType}
        />
      </Container>
      <AnalyticsBreakdownFilter filters={filters} />
      <AnalyticsBreakdownMetricTableHeader />
      {sections.map((section) => (
        <AnalyticsBreakdownMetricTableSection
          key={section.title}
          name={section.title}
          entries={section.entries}
          currency={currency}
          isYouTubeBrandConnect
        />
      ))}
    </Container>
  )
}
